/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useParams } from 'react-router';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import {
	TApiResponseUpdateSite,
	TBodyCreateSite,
	TBodyUpdateSite,
} from '../types';
import { TApiError } from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

// Update site
export const updateSite = (args: {
	id: string;
	body: TBodyUpdateSite;
}): Promise<TApiResponseUpdateSite> => {
	const { id, body } = args;
	return apiClient.patch(`/sites/${id}`, body);
};

/* -------------------------------------------------------------------------- */
/*                                   Hooks                                    */
/* -------------------------------------------------------------------------- */
export const useUpdateSite = (args: {
	page: number;
	size: number;
	mutationConfig?: TMutationConfig<typeof updateSite>;
}) => {
	const { page, size, mutationConfig } = args;
	const { projectId } = useParams();
	const { t } = useTranslation();

	const { onSuccess, ...restConfig } =
		mutationConfig || {};

	return useMutation({
		mutationFn: async (args: {
			id: string;
			body: TBodyCreateSite;
		}) => {
			return updateSite(args);
		},

		onSuccess: async (data, ...args) => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'sites.notifications.siteSuccessfullyUpdated'
				),
				color: 'success',
			});

			if (projectId) {
				queryClient.invalidateQueries({
					queryKey: [
						queryKeys.app.sites.list.key,
						{
							queryParams: {},
							searchParams: '',
						},
					],
				});
				queryClient.invalidateQueries({
					queryKey: [
						queryKeys.app.measurabilityProjects.list.key,
						projectId,
					],
				});
			} else {
				queryClient.invalidateQueries({
					queryKey: [
						queryKeys.app.sites.list.key,
						{ page: page, size: size },
					],
				});
			}

			onSuccess?.(data, ...args);
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});
};
